import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { getGravatarUrl, getInitialsFromEmail } from '../../../utils/gravatarUtils';
import Input from '../TailwindComponents/Input';
import Dropdown, { DropdownOption } from '../TailwindComponents/Dropdown';
import {
  PiUploadLight,
  PiSpinnerGapLight,
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiKeyboardLight,
} from 'react-icons/pi';
import FileUploader from '../TailwindComponents/FileUploader';
import { uploadUsersImport, getImportStatus } from '../../../services/import/importApi';
import { useImportPubSub } from '../../../hooks/useImportPubSub';
import type { ImportUserRow } from '../../../types/import';
import Button from '../TailwindComponents/Button';

interface InviteeItem {
  id: string;
  email: string;
  role: string;
}

interface PeopleEmailInputProps {
  id?: string;
  name?: string;
  label?: string;
  value: InviteeItem[];
  onChange: (value: InviteeItem[]) => void;
  roles: DropdownOption[];
  required?: boolean;
  disabled?: boolean;
}

type InputMode = 'manual' | 'upload';

const ALLOWED_EXTENSIONS = ['xlsx', 'xls', 'csv', 'json', 'txt', 'pdf', 'docx', 'md'];

const PeopleEmailInput: React.FC<PeopleEmailInputProps> = ({
  label,
  value = [],
  onChange,
  roles,
  required,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const [mode, setMode] = useState<InputMode>('manual');

  // File upload state
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [jobUuid, setJobUuid] = useState<string | null>(null);
  const [parseSuccess, setParseSuccess] = useState(false);
  const [parsedCount, setParsedCount] = useState(0);

  // PubSub hook for AI parsing progress
  const { progress, reset: resetProgress } = useImportPubSub({
    correlationId,
    enabled: isParsing,
    onPreviewReady: async (uuid) => {
      if (uuid || jobUuid) {
        await loadAndMergePreview(uuid || jobUuid!);
      }
    },
    onFailed: (err) => {
      setUploadError(err || t('AI parsing failed'));
      setIsParsing(false);
    },
  });

  // Load parsed preview and merge into invitee list
  const loadAndMergePreview = useCallback(async (uuid: string) => {
    try {
      const response = await getImportStatus(uuid);
      if (response.success && response.preview) {
        const rows = response.preview.rows as ImportUserRow[];
        const newInvitees = rows
          .filter((row) => row.email && !row.is_duplicate)
          .map((row, idx) => ({
            id: String(Date.now() + idx),
            email: row.email,
            role: row.role || 'full_access',
          }));

        // Merge with existing, skip duplicates
        const existingEmails = new Set(value.map((inv) => inv.email.toLowerCase()));
        const unique = newInvitees.filter(
          (inv) => !existingEmails.has(inv.email.toLowerCase())
        );

        if (unique.length > 0) {
          onChange([...value, ...unique]);
        }

        setParsedCount(unique.length);
        setParseSuccess(true);
        setIsParsing(false);
        setUploadFiles([]);

        // Auto-switch to manual mode after brief success message
        setTimeout(() => {
          setMode('manual');
          setParseSuccess(false);
          setParsedCount(0);
        }, 3000);
      }
    } catch {
      setUploadError(t('Failed to load parsed results'));
      setIsParsing(false);
    }
  }, [value, onChange, t]);

  const handleFilesChange = useCallback((files: File[]) => {
    setUploadFiles(files);
    setUploadError(null);
    setParseSuccess(false);
  }, []);

  const handleUploadAndParse = useCallback(async () => {
    const selectedFile = uploadFiles[0];
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const response = await uploadUsersImport(selectedFile);
      if (response.success && response.job_uuid) {
        setJobUuid(response.job_uuid);
        setCorrelationId(response.correlation_id || null);
        setIsUploading(false);
        setIsParsing(true);
      } else {
        const err = response.error;
        const msg = typeof err === 'string' ? err : typeof err === 'object' && err ? (err.description || err.title || t('Upload failed')) : t('Upload failed');
        setUploadError(msg);
        setIsUploading(false);
      }
    } catch (err: any) {
      const errorData = err.response?.data?.error;
      const errorMsg =
        typeof errorData === 'string'
          ? errorData
          : errorData?.description || errorData?.title || err.message || t('Upload failed');
      setUploadError(errorMsg);
      setIsUploading(false);
    }
  }, [uploadFiles, t]);

  const resetUpload = useCallback(() => {
    setUploadFiles([]);
    setIsUploading(false);
    setIsParsing(false);
    setUploadError(null);
    setCorrelationId(null);
    setJobUuid(null);
    setParseSuccess(false);
    setParsedCount(0);
    resetProgress();
  }, [resetProgress]);

  const domainSuffix = (() => {
    if (!userInfo?.Email) return '';
    const domain = userInfo.Email.split('@')[1];
    return domain ? `@${domain}` : '';
  })();

  const isValidLocalPart = (localPart: string): boolean => {
    if (!localPart) return false;
    const localPartRegex = /^[a-zA-Z0-9._-]+$/;
    return localPartRegex.test(localPart);
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputText = e.target.value;
    setInputError('');

    const lastChar = inputText[inputText.length - 1];

    if (lastChar === ',' || lastChar === ' ') {
      const emailLocalPart = inputText.slice(0, -1).trim();

      if (emailLocalPart) {
        if (!isValidLocalPart(emailLocalPart)) {
          setInputError(t('Email can only contain letters, numbers, dots, hyphens and underscores.'));
          return;
        }

        const fullEmail = `${emailLocalPart}${domainSuffix}`;
        if (isValidEmail(fullEmail)) {
          const emailExists = value.some(inv => inv.email === fullEmail);
          if (!emailExists) {
            const newId = String(value.length + 1);
            onChange([...value, { id: newId, email: fullEmail, role: '' }]);
            setInputValue('');
          } else {
            setInputError(t('This email is already added.'));
          }
        } else {
          setInputError(t('Invalid email format.'));
        }
      }
      return;
    }

    setInputValue(inputText);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const emailLocalPart = inputValue.trim();

      if (emailLocalPart) {
        if (!isValidLocalPart(emailLocalPart)) {
          setInputError(t('Email can only contain letters, numbers, dots, hyphens and underscores.'));
          return;
        }

        const fullEmail = `${emailLocalPart}${domainSuffix}`;
        if (isValidEmail(fullEmail)) {
          const emailExists = value.some(inv => inv.email === fullEmail);
          if (!emailExists) {
            const newId = String(value.length + 1);
            onChange([...value, { id: newId, email: fullEmail, role: '' }]);
            setInputValue('');
            setInputError('');
          } else {
            setInputError(t('This email is already added.'));
          }
        } else {
          setInputError(t('Invalid email format.'));
        }
      }
    } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      const lastInvitee = value[value.length - 1];
      const localPart = lastInvitee.email.split('@')[0];
      setInputValue(localPart);
      onChange(value.slice(0, -1));
      setInputError('');
    }
  };

  const handleRemoveInvitee = (id: string) => {
    onChange(value.filter(inv => inv.id !== id));
    setInputError('');
  };

  const handleRoleChange = (id: string, roleValue: string | string[]) => {
    onChange(
      value.map(inv =>
        inv.id === id
          ? { ...inv, role: Array.isArray(roleValue) ? roleValue[0] : roleValue }
          : inv
      )
    );
  };

  const displayValue = inputValue ? `${inputValue}${domainSuffix}` : '';

  return (
    <div className="flex-1">
      {label && (
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t(label)}
        </label>
      )}

      {/* Mode toggle commented out — Import Users uses modal wizard from AdminPage header */}
      {/* <ModeToggle> was here — manual/upload toggle removed */}

      {/* Manual email entry — always shown */}
      <div className="relative mb-4">
          <div className="flex items-center bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden focus-within:ring-1 focus-within:ring-primary-500 focus-within:border-primary-500 transition">
            <Input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              placeholder={t('Type email and press Enter or comma')}
              className="placeholder-gray-500 dark:placeholder-gray-400 !focus:outline-none !border-none !focus:ring-0"
              disabled={disabled}
            />
            {domainSuffix && (
              <span className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 font-medium bg-gray-100 dark:bg-gray-700 border-l border-gray-300 dark:border-gray-600">
                {displayValue.includes('@') ? displayValue.split('@')[1].startsWith(domainSuffix.substring(1)) ? domainSuffix : '' : domainSuffix}
              </span>
            )}
          </div>
          {inputError && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{inputError}</p>
          )}
        </div>

      {/* File upload mode — commented out, using ImportUsersWizard modal flow instead
      {mode === 'upload' && (
        <div className="mb-4">
          {parseSuccess && (
            <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
              <PiCheckCircleLight className="w-5 h-5 text-green-500 flex-shrink-0" />
              <p className="text-xs text-green-700 dark:text-green-300">
                {t('Successfully added {{count}} users from file', { count: parsedCount })}
              </p>
            </div>
          )}
          {uploadError && (
            <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
              <PiWarningCircleLight className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-700 dark:text-red-300 flex-1">{uploadError}</p>
              <button type="button" onClick={() => setUploadError(null)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
            </div>
          )}
          {isParsing && (
            <div className="flex flex-col items-center gap-3 py-8">
              <PiSpinnerGapLight className="w-8 h-8 text-blue-500 animate-spin" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('AI is parsing your file...')}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{progress.message || t('This usually takes 10-30 seconds')}</p>
            </div>
          )}
          {!isParsing && !parseSuccess && (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('AI will automatically parse names, emails, and roles from your file.')}</p>
              <FileUploader files={uploadFiles} onFilesChange={handleFilesChange} allowedExtensions={ALLOWED_EXTENSIONS} maxFiles={1} />
              {uploadFiles.length > 0 && (
                <Button type="button" onClick={handleUploadAndParse} disabled={isUploading} variant="primary" className="mt-3" size='small'>
                  {isUploading ? (<><PiSpinnerGapLight className="w-4 h-4 animate-spin" />{t('Uploading...')}</>) : (<><PiUploadLight className="w-4 h-4" />{t('Upload & Parse with AI')}</>)}
                </Button>
              )}
            </>
          )}
        </div>
      )}
      */}

      {/* Invitees List — always visible regardless of mode */}
      {value.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
            {t('Added')} ({value.length})
          </p>
          <div className="space-y-2">
            {value.map((invitee) => {
              const initials = getInitialsFromEmail(invitee.email);
              const gravatarUrl = getGravatarUrl(invitee.email, 40);

              return (
                <div key={invitee.id} className="flex gap-3 items-center bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                  <div className="relative w-10 h-10 flex-shrink-0">
                    <img
                      src={gravatarUrl}
                      alt={invitee.email}
                      className="w-10 h-10 rounded-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <div
                      className="hidden w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-semibold"
                    >
                      {initials}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-900 dark:text-gray-100 truncate">{invitee.email}</p>
                  </div>
                  <div className="w-48">
                    <Dropdown
                      options={roles}
                      placeholder={t('Select Role')}
                      value={invitee.role}
                      onChange={(roleValue) => handleRoleChange(invitee.id, roleValue)}
                      compact={true}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveInvitee(invitee.id)}
                    className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors flex-shrink-0"
                    title={t('Remove')}
                    disabled={disabled}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Info Message */}
      {value.length > 0 && !isParsing && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-xs text-blue-800 dark:text-blue-300">
            {value.length === 1
              ? t('Ready to send invitation to 1 person.')
              : t(`Ready to send invitations to ${value.length} people.`)}
          </p>
        </div>
      )}
    </div>
  );
};

export default PeopleEmailInput;
